import {
  Button,
  FormControl,
  FormLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { NotificationPolicyTypeEnum } from '@repo/api-gw-sdk';
import { useState } from 'react';

import type { FieldValidation } from '@/components/form/validation/fieldValidation';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';

import { NotificationPolicyTypes } from './data';
import type { UpsertNotificationPolicyState } from './upsertNotificationPolicyFlow';

import { Icon } from '../shared/icon';
import { Link } from '../shared/link';
import { TransactionalTextField } from '../shared/transactionalTextField';
import { StepContainer } from '../wizard/StepContainer';
import { RenameEntityName } from '../wizard/renameEntityName';

// ServiceNow field validations
const serviceNowValidation: Record<string, FieldValidation> = {
  instanceUrl: {
    validateInput: (value: string) => {
      if (!value.trim()) return false;
      try {
        const url = new URL(value.trim());
        // Match backend validation: must be https and have a valid host
        return url.protocol === 'https:' && url.host !== '';
      } catch {
        return false;
      }
    },
    errorMessage: 'Must be a valid HTTPS URL',
  },
  username: {
    validateInput: (value: string) => value.trim().length >= 1,
    errorMessage: 'Username is required',
  },
  password: {
    validateInput: (value: string) => value.length >= 1,
    errorMessage: 'Password is required',
  },
};

export const NotificationPolicyCredentials = (
  props: StepProps<UpsertNotificationPolicyState>
) => {
  const dal = useDAL();
  const [error, setError] = useState<string | undefined>();
  const [saveInProgress, setSaveInProgress] = useState(false);
  const [customHeadersEnabled, setCustomHeadersEnabled] = useState(
    () =>
      !!props.wizardState.config?.webhook?.headers &&
      Object.keys(props.wizardState.config.webhook.headers).length > 0
  );

  // ServiceNow field touched states
  const [serviceNowTouched, setServiceNowTouched] = useState({
    instanceUrl: false,
    username: false,
    password: false,
  });

  const next = async () => {
    props.setNextStep(props.currentStep.next?.[0]);
  };

  const finish = async () => {
    setSaveInProgress(true);
    setError(undefined);

    try {
      // Filter out empty headers before saving
      let stateToSave = props.wizardState;
      if (
        stateToSave.type === NotificationPolicyTypeEnum.Webhook &&
        stateToSave.config?.webhook?.headers
      ) {
        const filteredHeaders = Object.entries(
          stateToSave.config.webhook.headers
        ).reduce<Record<string, string>>((acc, [key, value]) => {
          if (key.trim() && value.trim()) {
            acc[key] = value;
          }
          return acc;
        }, {});

        stateToSave = {
          ...stateToSave,
          config: {
            ...stateToSave.config,
            webhook: {
              ...stateToSave.config.webhook,
              headers: filteredHeaders,
            },
          },
        };
      }

      if (props.wizardState.id) {
        await dal.notifications.policies.update(stateToSave);
        await props.finish();
      } else {
        await dal.notifications.policies.create(stateToSave);
        await props.finish();
      }
    } catch (e) {
      const errorMessage =
        (e as { body?: { error?: string } })?.body?.error ||
        (e as Error).message ||
        'Unknown error';
      setError(`Failed to save notification policy: ${errorMessage}`);
    } finally {
      setSaveInProgress(false);
    }
  };

  const validateParams = () => {
    if (saveInProgress) {
      return false;
    }

    if (props.wizardState.type === NotificationPolicyTypeEnum.Email) {
      return !!props.wizardState.config?.email?.email;
    }

    if (props.wizardState.type === NotificationPolicyTypeEnum.Sns) {
      return !!props.wizardState.config?.sns?.topicArn;
    }

    if (props.wizardState.type === NotificationPolicyTypeEnum.Splunk) {
      return !!props.wizardState.config?.splunk?.token;
    }

    if (props.wizardState.type === NotificationPolicyTypeEnum.Slack) {
      return (
        !!props.wizardState.config?.slack?.channel &&
        !!props.wizardState.config?.slack?.token
      );
    }

    if (props.wizardState.type === NotificationPolicyTypeEnum.Webhook) {
      if (!props.wizardState.config?.webhook?.url) {
        return false;
      }

      // If custom headers toggle is enabled, validate all headers
      if (customHeadersEnabled) {
        const headers = props.wizardState.config?.webhook?.headers || {};
        const headerEntries = Object.entries(headers);

        // Must have at least one header
        if (headerEntries.length === 0) {
          return false;
        }

        // All headers must have both key AND value (or both empty)
        const allHeadersValid = headerEntries.every(([key, value]) => {
          const keyTrimmed = key.trim();
          const valueTrimmed = value.trim();
          // Both empty is OK (will be filtered out), or both filled is OK
          return (
            (keyTrimmed === '' && valueTrimmed === '') ||
            (keyTrimmed !== '' && valueTrimmed !== '')
          );
        });

        // Must have at least one non-empty header
        const hasValidHeader = headerEntries.some(
          ([key, value]) => key.trim() !== '' && value.trim() !== ''
        );

        return allHeadersValid && hasValidHeader;
      }

      return true;
    }

    if (props.wizardState.type === NotificationPolicyTypeEnum.ServiceNow) {
      const config = props.wizardState.config?.serviceNow;
      return (
        serviceNowValidation.instanceUrl.validateInput(
          config?.instanceUrl || ''
        ) &&
        serviceNowValidation.username.validateInput(config?.username || '') &&
        serviceNowValidation.password.validateInput(config?.password || '')
      );
    }

    return false;
  };

  return (
    <StepContainer
      hideStepper
      canGoNext={validateParams}
      nextButtonText={
        props.wizardState.type !== NotificationPolicyTypeEnum.Splunk
          ? 'Next'
          : props.wizardState.id
            ? 'Save'
            : 'Test and Create'
      }
      onNextClick={
        props.wizardState.type === NotificationPolicyTypeEnum.Splunk
          ? finish
          : next
      }
      navigationComponent={
        error ? (
          <Stack alignItems='center' direction='row' sx={{ color: 'red' }}>
            <i className='ri-error-warning-line mr-[8px] text-[18px]' />
            {error}
          </Stack>
        ) : (
          <RenameEntityName
            label='Notification policy name'
            name={props.wizardState.name}
            onSubmit={(name) =>
              props.setWizardState((state) => ({ ...state, name }))
            }
          />
        )
      }
    >
      <Stack
        sx={{ height: '100%' }}
        alignItems='center'
        justifyContent='center'
        gap='24px'
      >
        <Typography
          variant='h3'
          sx={{ textAlign: 'center', marginBottom: '16px' }}
        >
          {NotificationPolicyTypes[props.wizardState.type]?.parametersTitle}
        </Typography>

        {props.wizardState.type === NotificationPolicyTypeEnum.Email && (
          <Stack className='w-[582px]' gap='8px'>
            <Typography>Email</Typography>
            <TextField
              value={props.wizardState.config?.email?.email || ''}
              placeholder='Example: yourname@example.com'
              onChange={(event) =>
                props.setWizardState((state) => ({
                  ...state,
                  config: {
                    email: {
                      email: event.target.value,
                    },
                  },
                }))
              }
            />
          </Stack>
        )}
        {props.wizardState.type === NotificationPolicyTypeEnum.Sns && (
          <Stack className='w-[582px]' gap='8px'>
            <Typography>Topic ARN</Typography>
            <TextField
              value={props.wizardState.config?.sns?.topicArn || ''}
              placeholder='Example: arn:aws:sns:us-east-1:123456789012:eon-notifications'
              onChange={(event) =>
                props.setWizardState((state) => ({
                  ...state,
                  config: {
                    sns: {
                      topicArn: event.target.value,
                    },
                  },
                }))
              }
            />
            <Typography
              fontSize={'12px'}
              variant='subtitle2'
            >{`Copy this from the SNS topic details page.`}</Typography>
          </Stack>
        )}
        {props.wizardState.type === NotificationPolicyTypeEnum.Splunk && (
          <Stack className='w-[582px]' gap='8px'>
            <Typography>Splunk HEC Url</Typography>
            <TextField
              value={props.wizardState.config?.splunk?.url || ''}
              onChange={(event) =>
                props.setWizardState((state) => ({
                  ...state,
                  config: {
                    splunk: {
                      token: state.config?.splunk?.token,
                      url: event.target.value,
                    },
                  },
                }))
              }
            />
            <Typography>Token</Typography>
            <TextField
              value={props.wizardState.config?.splunk?.token || ''}
              onChange={(event) =>
                props.setWizardState((state) => ({
                  ...state,
                  config: {
                    splunk: {
                      url: state.config?.splunk?.url,
                      token: event.target.value,
                    },
                  },
                }))
              }
            />
          </Stack>
        )}
        {props.wizardState.type === NotificationPolicyTypeEnum.Slack && (
          <>
            <Stack className='w-[582px]' gap='8px'>
              <Typography>Channel ID</Typography>
              <TextField
                value={props.wizardState.config?.slack?.channel || ''}
                placeholder='Example: D07GNGUCLUW'
                onChange={(event) =>
                  props.setWizardState((state) => ({
                    ...state,
                    config: {
                      slack: {
                        token: state.config?.slack?.token,
                        channel: event.target.value,
                      },
                    },
                  }))
                }
              />
              <Typography
                fontSize={'12px'}
                variant='subtitle2'
              >{`Find this in Slack's channel management dialog by clicking the channel's title and scrolling to the bottom of the tab.`}</Typography>
            </Stack>
            <Stack className='w-[582px]' gap='8px'>
              <Typography>Bot user OAuth token</Typography>
              <TextField
                value={props.wizardState.config?.slack?.token || ''}
                onChange={(event) =>
                  props.setWizardState((state) => ({
                    ...state,
                    config: {
                      slack: {
                        channel: state.config?.slack?.channel,
                        token: event.target.value,
                      },
                    },
                  }))
                }
              />
              <Typography fontSize={'12px'} variant='subtitle2'>
                Copy this from the OAuth & Permissions page in your&nbsp;
                <Link target='_blank' href='https://api.slack.com/apps'>
                  Slack app&apos;s
                </Link>
                &nbsp;configuration.
              </Typography>
            </Stack>
          </>
        )}
        {props.wizardState.type === NotificationPolicyTypeEnum.Webhook && (
          <>
            <Stack className='w-[582px]' gap='8px'>
              <Typography>Webhook URL</Typography>
              <TextField
                required
                value={props.wizardState.config?.webhook?.url || ''}
                placeholder='Example: https://your-webhook-endpoint.com/notify'
                onChange={(event) =>
                  props.setWizardState((state) => ({
                    ...state,
                    config: {
                      webhook: {
                        url: event.target.value,
                        headers: customHeadersEnabled
                          ? state.config?.webhook?.headers || {}
                          : {},
                      },
                    },
                  }))
                }
              />
              <Typography
                fontSize={'12px'}
                variant='subtitle2'
              >{`The URL where webhook notifications will be sent.`}</Typography>
            </Stack>
            <Stack className='w-[582px]' gap='24px'>
              <Stack
                direction='row'
                justifyContent='space-between'
                alignItems='center'
              >
                <Typography className='font-semibold' variant='body1'>
                  Custom headers
                </Typography>
                <Switch
                  size='small'
                  checked={customHeadersEnabled}
                  onChange={() => {
                    const newValue = !customHeadersEnabled;
                    setCustomHeadersEnabled(newValue);
                    if (!newValue) {
                      // When disabling, clear all headers
                      props.setWizardState((state) => ({
                        ...state,
                        config: {
                          webhook: {
                            url: state.config?.webhook?.url || '',
                            headers: {},
                          },
                        },
                      }));
                    }
                  }}
                />
              </Stack>
              {customHeadersEnabled && (
                <Stack gap='40px'>
                  {Object.entries(
                    props.wizardState.config?.webhook?.headers || {}
                  ).length > 0 && (
                    <Stack gap='24px'>
                      {Object.entries(
                        props.wizardState.config?.webhook?.headers || {}
                      ).map(([key, value]) => (
                        <Stack
                          key={key}
                          direction='row'
                          gap='24px'
                          alignItems='end'
                          width='560px'
                        >
                          <FormControl variant='outlined' fullWidth>
                            <FormLabel>Key</FormLabel>
                            <TransactionalTextField
                              initValue={key}
                              onChange={(newKey) => {
                                if (key !== newKey) {
                                  const headers =
                                    props.wizardState.config?.webhook
                                      ?.headers || {};
                                  const newHeaders = {
                                    ...headers,
                                    [newKey]: headers[key],
                                  };
                                  // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
                                  delete newHeaders[key];
                                  props.setWizardState((state) => ({
                                    ...state,
                                    config: {
                                      webhook: {
                                        url: state.config?.webhook?.url || '',
                                        headers: newHeaders,
                                      },
                                    },
                                  }));
                                }
                              }}
                            />
                          </FormControl>
                          <FormControl variant='outlined' fullWidth>
                            <FormLabel>Value</FormLabel>
                            <TransactionalTextField
                              initValue={value}
                              onChange={(newValue) => {
                                const headers =
                                  props.wizardState.config?.webhook?.headers ||
                                  {};
                                const newHeaders = {
                                  ...headers,
                                  [key]: newValue,
                                };
                                props.setWizardState((state) => ({
                                  ...state,
                                  config: {
                                    webhook: {
                                      url: state.config?.webhook?.url || '',
                                      headers: newHeaders,
                                    },
                                  },
                                }));
                              }}
                            />
                          </FormControl>
                          <Stack height='100%'>
                            <Icon
                              iconClass='material-symbols-close-rounded'
                              onClick={() => {
                                const headers =
                                  props.wizardState.config?.webhook?.headers ||
                                  {};
                                const newHeaders = { ...headers };
                                // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
                                delete newHeaders[key];
                                props.setWizardState((state) => ({
                                  ...state,
                                  config: {
                                    webhook: {
                                      url: state.config?.webhook?.url || '',
                                      headers: newHeaders,
                                    },
                                  },
                                }));
                              }}
                            />
                          </Stack>
                        </Stack>
                      ))}
                    </Stack>
                  )}
                  <Button
                    sx={{ width: 'max-content' }}
                    variant='outlined'
                    onClick={() => {
                      const headers =
                        props.wizardState.config?.webhook?.headers || {};
                      props.setWizardState((state) => ({
                        ...state,
                        config: {
                          webhook: {
                            url: state.config?.webhook?.url || '',
                            headers: { ...headers, '': '' },
                          },
                        },
                      }));
                    }}
                  >
                    + Add Header
                  </Button>
                  <Typography
                    fontSize={'12px'}
                    variant='subtitle2'
                    sx={{ color: 'text.secondary' }}
                  >
                    {`Add custom headers for authentication (e.g., Authorization, X-API-Key). At least one header with both key and value is required.`}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </>
        )}
        {props.wizardState.type === NotificationPolicyTypeEnum.ServiceNow && (
          <>
            <Stack className='w-[582px]' gap='8px'>
              <Typography>Instance URL</Typography>
              <TextField
                value={props.wizardState.config?.serviceNow?.instanceUrl || ''}
                placeholder='https://yourcompany.service-now.com'
                onBlur={() =>
                  setServiceNowTouched((prev) => ({
                    ...prev,
                    instanceUrl: true,
                  }))
                }
                error={
                  serviceNowTouched.instanceUrl &&
                  !serviceNowValidation.instanceUrl.validateInput(
                    props.wizardState.config?.serviceNow?.instanceUrl || ''
                  )
                }
                helperText={
                  serviceNowTouched.instanceUrl &&
                  !serviceNowValidation.instanceUrl.validateInput(
                    props.wizardState.config?.serviceNow?.instanceUrl || ''
                  )
                    ? serviceNowValidation.instanceUrl.errorMessage
                    : 'Your ServiceNow instance URL.'
                }
                onChange={(event) =>
                  props.setWizardState((state) => ({
                    ...state,
                    config: {
                      serviceNow: {
                        instanceUrl: event.target.value,
                        username: state.config?.serviceNow?.username,
                        password: state.config?.serviceNow?.password,
                      },
                    },
                  }))
                }
              />
            </Stack>
            <Stack className='w-[582px]' gap='8px'>
              <Typography>Username</Typography>
              <TextField
                value={props.wizardState.config?.serviceNow?.username || ''}
                placeholder='API username'
                onBlur={() =>
                  setServiceNowTouched((prev) => ({ ...prev, username: true }))
                }
                error={
                  serviceNowTouched.username &&
                  !serviceNowValidation.username.validateInput(
                    props.wizardState.config?.serviceNow?.username || ''
                  )
                }
                helperText={
                  serviceNowTouched.username &&
                  !serviceNowValidation.username.validateInput(
                    props.wizardState.config?.serviceNow?.username || ''
                  )
                    ? serviceNowValidation.username.errorMessage
                    : 'ServiceNow API username with permissions to create incidents.'
                }
                onChange={(event) =>
                  props.setWizardState((state) => ({
                    ...state,
                    config: {
                      serviceNow: {
                        instanceUrl: state.config?.serviceNow?.instanceUrl,
                        username: event.target.value,
                        password: state.config?.serviceNow?.password,
                      },
                    },
                  }))
                }
              />
            </Stack>
            <Stack className='w-[582px]' gap='8px'>
              <Typography>Password</Typography>
              <TextField
                type='password'
                value={props.wizardState.config?.serviceNow?.password || ''}
                placeholder='API password'
                onBlur={() =>
                  setServiceNowTouched((prev) => ({ ...prev, password: true }))
                }
                error={
                  serviceNowTouched.password &&
                  !serviceNowValidation.password.validateInput(
                    props.wizardState.config?.serviceNow?.password || ''
                  )
                }
                helperText={
                  serviceNowTouched.password &&
                  !serviceNowValidation.password.validateInput(
                    props.wizardState.config?.serviceNow?.password || ''
                  )
                    ? serviceNowValidation.password.errorMessage
                    : 'ServiceNow API password.'
                }
                onChange={(event) =>
                  props.setWizardState((state) => ({
                    ...state,
                    config: {
                      serviceNow: {
                        instanceUrl: state.config?.serviceNow?.instanceUrl,
                        username: state.config?.serviceNow?.username,
                        password: event.target.value,
                      },
                    },
                  }))
                }
              />
            </Stack>
          </>
        )}
      </Stack>
    </StepContainer>
  );
};
